import { useEffect, useMemo, useState } from 'react';
import { apiUrl } from '../../utils/apiBase';
import { generateShortCode } from '../../utils/shortCodeUtils';

// link interno do card (o /c/<código> curto dá redirecionamento em loop em alguns acessos)
const linkCard = (flowId: string | undefined, cardId: string) =>
  flowId ? `/flows/${flowId}/card/${generateShortCode(cardId)}` : `/?card=${encodeURIComponent(cardId)}`;

// 🧾 ND vinculada (30/10/2026, pedido do Samuel Branco, ok do André)
// Campo tipo 'nd_vinculada': o comprador aponta, num card de compra, a Nota de Débito (verba do
// fornecedor, fluxo "Verbas e Notas de Débito") que paga/abate a compra. Uma ND por card; o valor
// usado fica no campo "Valor usado da ND" (valor parcial permitido). Ao salvar o card o servidor
// grava o vínculo (coleção nd_vinculos): o card da ND lista as compras e o Painel de Verbas abate.
// Valor guardado: [{ verbaCardId, verbaFlowId, nd, fornecedor, titulo, rotulo }] (lista, para os
// lugares genéricos que mostram campos não quebrarem com objeto solto).

export interface NdSelecionada { verbaCardId: string; verbaFlowId?: string; nd?: string; fornecedor?: string; titulo?: string; rotulo?: string; }
export interface NdConfig { verbasFlowId?: string; valorUsadoCampoId?: string; valorCardCampoId?: string; }
interface Opcao { id: string; titulo: string; etapa: string; nd: string; fornecedor: string; valorNd: number; usado: number; disponivel: number; }

export const ehNdVinculada = (v: any): boolean =>
  Array.isArray(v) && v.length > 0 && !!v[0] && typeof v[0] === 'object' && 'verbaCardId' in v[0];
export const textoNd = (v: any): string => {
  if (!ehNdVinculada(v)) return '';
  const x = v[0] as NdSelecionada;
  return x.rotulo || [ndTxt(x.nd) || x.titulo, x.fornecedor].filter(Boolean).join(' – ');
};

const brl = (n: number) => (Number(n) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const norm = (s: string) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const numero = (v: unknown) => {
  if (v == null || v === '') return 0;
  if (typeof v === 'number') return v;
  const s = String(v).replace(/[^\d,.-]/g, '');
  const n = s.includes(',') ? Number(s.replace(/\./g, '').replace(',', '.')) : Number(s);
  return isNaN(n) ? 0 : n;
};
// "ND 130826" / "FALTA ND" vêm da planilha: só vira "ND nº" quando tem número
export const ndTxt = (nd?: string) => (nd && /\d/.test(nd) ? `ND ${nd.replace(/^n\.?\s*d\.?\s*/i, '').trim()}` : '');
const rotuloDe = (o: Opcao) => [ndTxt(o.nd) || o.titulo, o.fornecedor].filter(Boolean).join(' – ');

interface Props {
  valor: any;
  onChange: (novo: NdSelecionada[] | '') => void;
  podeEditar: boolean;
  config?: NdConfig;
  compraFlowId?: string;
  compraCardId?: string;
  /** valor atual do campo "Valor usado da ND" e do valor do card (para o padrão ao escolher) */
  valorUsado?: any;
  valorCard?: any;
  onValorUsado?: (v: string) => void;
}

export default function CampoNdVinculada({ valor, onChange, podeEditar, config, compraFlowId, compraCardId, valorUsado, valorCard, onValorUsado }: Props) {
  const atual: NdSelecionada | null = ehNdVinculada(valor) ? valor[0] : null;
  const [opcoes, setOpcoes] = useState<Opcao[] | null>(null);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    if (!compraFlowId) return;
    if (!podeEditar && !atual) return;
    let vivo = true;
    const p = new URLSearchParams({ compraFlowId });
    if (config?.verbasFlowId) p.set('verbasFlowId', config.verbasFlowId);
    if (compraCardId) p.set('compraCardId', String(compraCardId));
    fetch(apiUrl('/verbas-nd/opcoes') + '?' + p.toString())
      .then(r => r.json())
      .then(j => { if (vivo) { if (j.success) setOpcoes(j.opcoes || []); else setErro(j.error || 'erro ao carregar as NDs'); } })
      .catch(() => { if (vivo) setErro('erro ao carregar as NDs'); });
    return () => { vivo = false; };
  }, [compraFlowId, compraCardId, config?.verbasFlowId, podeEditar, !!atual]); // eslint-disable-line react-hooks/exhaustive-deps

  const infoAtual = atual && opcoes ? opcoes.find(o => o.id === atual.verbaCardId) : undefined;
  const usadoAqui = numero(valorUsado);
  const filtradas = useMemo(() => {
    const q = norm(busca).trim();
    return (opcoes || []).filter(o => !q || norm(`${o.nd} ${o.fornecedor} ${o.titulo}`).includes(q)).slice(0, 60);
  }, [opcoes, busca]);

  const escolher = (o: Opcao) => {
    onChange([{ verbaCardId: o.id, verbaFlowId: config?.verbasFlowId, nd: o.nd, fornecedor: o.fornecedor, titulo: o.titulo, rotulo: rotuloDe(o) }]);
    if (onValorUsado && !numero(valorUsado)) {
      const padrao = numero(valorCard);
      if (padrao) onValorUsado(padrao.toFixed(2));
    }
    setAberto(false); setBusca('');
  };

  return (
    <div className="space-y-1.5">
      {atual ? (
        <div className="flex items-start gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs">
          <span className="text-base leading-none">🧾</span>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-900 break-words">{textoNd(valor)}</div>
            {atual.titulo && ndTxt(atual.nd) && <div className="text-[11px] text-gray-500 break-words">{atual.titulo}</div>}
            {infoAtual && (
              <div className="text-[11px] text-gray-600 mt-0.5">
                ND {brl(infoAtual.valorNd)} · já usado em outras compras {brl(infoAtual.usado)} · saldo antes desta {brl(infoAtual.disponivel)}
                {usadoAqui > 0 && <> · <b className={usadoAqui > infoAtual.disponivel + 0.005 ? 'text-red-600' : 'text-emerald-700'}>
                  depois desta {brl(infoAtual.disponivel - usadoAqui)}</b></>}
              </div>
            )}
            {opcoes && !infoAtual && <div className="text-[11px] text-gray-500 mt-0.5">ND já finalizada ou cancelada no fluxo de Verbas.</div>}
            <a href={linkCard(atual.verbaFlowId || config?.verbasFlowId, atual.verbaCardId)} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-600 hover:underline">
              abrir a ND #{generateShortCode(atual.verbaCardId)}</a>
          </div>
          {podeEditar && (
            <div className="flex flex-col gap-1">
              <button type="button" onClick={() => setAberto(a => !a)} className="text-[11px] px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-50">Trocar</button>
              <button type="button" onClick={() => { if (confirm('Desvincular a ND deste card?')) onChange(''); }} className="text-[11px] px-2 py-0.5 rounded border border-red-200 text-red-600 bg-white hover:bg-red-50">Remover</button>
            </div>
          )}
        </div>
      ) : podeEditar ? (
        <button type="button" onClick={() => setAberto(a => !a)} className="w-full text-left px-3 py-1.5 text-xs border border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-500 bg-white">
          🧾 Vincular uma ND (verba do fornecedor)…</button>
      ) : (
        <div className="px-3 py-2 bg-white border border-gray-200 rounded text-xs text-gray-400">—</div>
      )}

      {podeEditar && aberto && (
        <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
          <input autoFocus value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por fornecedor ou nº da ND…"
            className="w-full px-2.5 py-1.5 text-xs border-b border-gray-200 rounded-t-lg focus:outline-none" />
          <div className="max-h-64 overflow-y-auto divide-y divide-gray-50">
            {erro && <div className="px-3 py-2 text-xs text-red-600">{erro}</div>}
            {!erro && !opcoes && <div className="px-3 py-2 text-xs text-gray-400">Carregando NDs…</div>}
            {opcoes && !filtradas.length && <div className="px-3 py-2 text-xs text-gray-400">Nenhuma ND em aberto com esse filtro.</div>}
            {filtradas.map(o => (
              <button key={o.id} type="button" onClick={() => escolher(o)}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${atual?.verbaCardId === o.id ? 'bg-emerald-50' : ''}`}>
                <div className="flex items-center gap-2">
                  <b className="text-gray-900">{ndTxt(o.nd) || 'sem nº de ND'}</b>
                  <span className="text-gray-700 truncate">– {o.fornecedor || '(sem fornecedor)'}</span>
                  <span className={`ml-auto whitespace-nowrap font-semibold ${o.disponivel > 0 ? 'text-emerald-700' : 'text-red-600'}`}>saldo {brl(o.disponivel)}</span>
                </div>
                <div className="text-[10px] text-gray-500 truncate">{o.titulo} · {o.etapa} · ND {brl(o.valorNd)}{o.usado ? ` · usado ${brl(o.usado)}` : ''}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Bloco no card da ND: compras do fluxo de compras pagas/abatidas com ela. */
export function ComprasDaNd({ verbaCardId, verbasFlowId, empresaId }: { verbaCardId: string; verbasFlowId?: string; empresaId?: string }) {
  const [dados, setDados] = useState<{ vinculos: any[]; valorNd: number; usado: number; disponivel: number } | null>(null);
  useEffect(() => {
    if (!verbaCardId || !empresaId) return;
    let vivo = true;
    const p = new URLSearchParams({ verbaCardId: String(verbaCardId), empresaId: String(empresaId) });
    if (verbasFlowId) p.set('verbasFlowId', verbasFlowId);
    fetch(apiUrl('/verbas-nd/vinculos') + '?' + p.toString()).then(r => r.json())
      .then(j => { if (vivo && j.success) setDados(j); }).catch(() => {});
    return () => { vivo = false; };
  }, [verbaCardId, verbasFlowId, empresaId]);
  if (!dados || !dados.vinculos.length) return null;
  return (
    <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/40 p-3">
      <div className="text-xs font-semibold text-gray-800 mb-1">🧾 Compras pagas/abatidas com esta ND</div>
      <div className="text-[11px] text-gray-600 mb-2">
        ND {brl(dados.valorNd)} · usado {brl(dados.usado)} · <b className={dados.disponivel < 0 ? 'text-red-600' : 'text-emerald-700'}>saldo {brl(dados.disponivel)}</b>
      </div>
      <div className="space-y-1">
        {dados.vinculos.map((v: any) => (
          <div key={v.compraCardId} className="flex items-start gap-2 text-xs bg-white border border-gray-200 rounded px-2 py-1.5">
            <div className="flex-1 min-w-0">
              <a href={linkCard(v.compraFlowId, v.compraCardId)} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-700 hover:underline break-words">
                {v.compraTitulo || 'Card de compra'} #{generateShortCode(v.compraCardId)}</a>
              <div className="text-[10px] text-gray-500">
                {(v.criadoEm || v.atualizadoEm) ? new Date(v.atualizadoEm || v.criadoEm).toLocaleString('pt-BR') : ''}{v.por ? ` · ${v.por}` : ''}</div>
            </div>
            <span className="font-semibold text-gray-900 whitespace-nowrap">{brl(v.valor)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
